import {
  AsyncPipe,
  DOCUMENT,
  FormStyle,
  getLocaleDayNames,
  NgClass,
  NgIf,
  TranslationWidth,
} from '@angular/common';
import { TagManagerService } from '@sc/feature-tag-manager';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  Appointment,
  AppointmentsGroupBy,
  BoundingBox,
  CLIENT_APP_ID,
  ClientAppId,
  CountryBounds,
  Dossier,
  DotFitter,
  FilterDisplayStyle,
  Fitter,
  GeoCoordinates,
  InterventionParams,
  LocationData,
  LocationDataGeocoded,
  SearchAvance,
} from '@sc/data-access-models';
import {
  DossierSandboxService,
  InsuranceCoverSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import { StorageService } from '@sc/data-access-storage';
import { TranslatorService } from '@sc/data-access-translation';
import { AdaptableOverlayService } from '@sc/feature-adaptable-overlay';
import {
  AppointmentsSubService,
  AppointmentSubscriptionBuilder,
} from '@sc/feature-appointments-subscription';
import { BottomSheetService } from '@sc/feature-bottom-sheet';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { FocusManagerService } from '@sc/feature-focus-manager';
import { LoadingService } from '@sc/feature-loader';
import {
  gmapBoundsToBoundingBox,
  GmapComponent,
  MapOnBoundsChangeEvent,
  MapOnCenterChangeEvent,
  MapOnZoomChangeEvent,
} from '@sc/feature-map';
import {
  AddressSearchBarComponent,
  AppointmentCommonListComponent,
  AppointmentFiltersComponent,
  ButtonComponent,
  CalendarComponent,
  CustomValidators,
  IconComponent,
} from '@sc/ui-components';
import {
  AppointmentUtils,
  dayjs,
  DeclarationConditions,
  getTranslatableDuration,
  ScreenUtils,
} from '@sc/utils';
import { throttle } from 'lodash-es';
import { TourAnchorMatMenuDirective } from 'ngx-ui-tour-md-menu';
import { BehaviorSubject, EMPTY, Observable, of, Subject } from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  switchMap,
  switchMapTo,
  take,
  tap,
} from 'rxjs/operators';
import { MatTabsModule } from '@angular/material/tabs';

const DEBUG = false;

@Component({
  selector: 'sc-search-page',
  styleUrls: ['./search-page.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AppointmentSubscriptionBuilder],
  imports: [
    NgIf,
    MatTabsModule,
    MatTooltipModule,
    IconComponent,
    AddressSearchBarComponent,
    MatExpansionModule,
    AppointmentFiltersComponent,
    AppointmentCommonListComponent,
    CalendarComponent,
    GmapComponent,
    TourAnchorMatMenuDirective,
    NgClass,
    ButtonComponent,
    AsyncPipe,
    TranslateModule,
  ],
  template: `
    <div class="top">
      <span class="left-title-container">
        <h1
          *ngIf="showListTitle"
          class="list-title sc-design-title-h1"
          [innerHTML]="
            'CustomerDeclaration.AppointmentSearchPage.title' | translate
          "
        ></h1>
        <button
          id="gpdrInfoTooltipTop"
          class="sc-design-text-4-bold"
          #tooltip="matTooltip"
          [matTooltip]="
            'CustomerDeclaration.AppointmentSearchPage.rankingSeo' | translate
          "
          matTooltipPosition="right"
          (click)="onGpdrInfo()"
          data-html="true"
        >
          {{ 'CustomerDeclaration.AppointmentSearchPage.gpdrInfo' | translate
          }}<sc-icon name="info"></sc-icon>
        </button>
      </span>

      <sc-address-search-bar
        *ngIf="shouldShowSearchBar()"
        id="search-bar-container"
        [selectedSearchLocationGeoCodedData]="
          selectedSearchLocationGeoCodedData
        "
        [form]="form"
        [suggestedLocationsData]="googleAutoCompleteResponse"
        [hasOutsideInterventionFeature]="hasOutsideInterventionFeature"
        [loading]="loading$ | async"
        (updateAddressSuggestionsEvent)="onUpdateAddressSuggestions($event)"
        (searchClickEvent)="onClickAddressButton()"
        (selectedAddressEvent)="onAddressChange($event, true)"
      >
      </sc-address-search-bar>
      <mat-accordion
        *ngIf="shouldShowTopFilters() && !(noBookDisplay$ | async)"
        class="mobile-filter-accordion"
      >
        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <p class="sc-design-text-3-bold">
              {{
                'CustomerDeclaration.AppointmentSearchPage.mobileFilterAccordionTitle'
                  | translate
              }}
            </p>
          </mat-expansion-panel-header>

          <sc-appointment-filters
            id="search-page-filters"
            [displayListMapToggle]="false"
            [displayStyle]="filterStyle"
            [displayTimeFilters]="allowPeriodFilter"
            [displayOutsideInterventionFilters]="hasOutsideInterventionFeature"
            [displayReplacementVehicleFilters]="displayReplacementVehicle"
            [displayGeneralistsFilter]="displayGeneralistsToggle | async"
            [displayGlassSpecialistFilter]="displayGeneralistsToggle | async"
            [configurationTimeFilters]="
              [null].concat(configurationService.getConfig().timeFilters)
            "
            (changeListMapToggle)="onToggleChange($event)"
          ></sc-appointment-filters>
        </mat-expansion-panel>
      </mat-accordion>
    </div>
    <div class="sides">
      <div *ngIf="shouldShowLeftSide()" class="left">
        <span class="left-title-container"
          ><h1
            *ngIf="showListTitle"
            class="list-title sc-design-title-h1"
            [innerHTML]="
              'CustomerDeclaration.AppointmentSearchPage.title' | translate
            "
          ></h1>
          <button
            id="gpdrInfoTooltipSide"
            class="sc-design-text-4-bold"
            #tooltip="matTooltip"
            [matTooltip]="'GlobalApplicativeConstants.gdpr' | translate"
            matTooltipPosition="right"
            (click)="onGpdrInfo()"
            data-html="true"
          >
            {{ 'CustomerDeclaration.AppointmentSearchPage.gpdrInfo' | translate
            }}<sc-icon name="info"></sc-icon>
          </button>
        </span>
        <sc-appointment-filters
          *ngIf="
            shouldShowLeftFilters() &&
            !(noBookDisplay$ | async) &&
            !isMobileResolution
          "
          id="search-page-filters"
          [displayListMapToggle]="false"
          [displayStyle]="filterStyle"
          [displayTimeFilters]="allowPeriodFilter"
          [displayOutsideInterventionFilters]="hasOutsideInterventionFeature"
          [displayReplacementVehicleFilters]="displayReplacementVehicle"
          [displayGeneralistsFilter]="displayGeneralistsToggle | async"
          [displayGlassSpecialistFilter]="displayGeneralistsToggle | async"
          [configurationTimeFilters]="
            [null].concat(configurationService.getConfig().timeFilters)
          "
          (changeListMapToggle)="onToggleChange($event)"
        ></sc-appointment-filters>

        <mat-tab-group *ngIf="isMobileResolution">
          <mat-tab>
            <ng-template mat-tab-label>{{
              'CustomerDeclaration.AppointmentSearchPage.toggleList' | translate
            }}</ng-template>
            <section>
              <sc-appointment-common-list
                id="search-page-appointment-list"
                [filters]="filters$ | async"
                [groupBy]="
                  (dossier$ | async)?.searchCriteria?.filters?.groupBy ||
                  'fitters'
                "
                [selectedAppointment]="selectedAppointment"
                [displayReplacementVehicle]="displayReplacementVehicle"
                [canFilterOutsideIntervention]="hasOutsideInterventionFeature"
                [selectedDay]="selectedDay"
                [noBookDisplay]="noBookDisplay$ | async"
                [highlightBestScoreFitter]="highlightBestScoreFitter"
                [appointmentSubscription]="appointmentSub"
                [loading]="loading$ | async"
                [dossier]="dossier$ | async"
                [shouldDelayNoSlotFitterDisplay]="
                  configurationService?.hasNoSlotFitterWithDelayDisplay()
                "
                (slotClickEvent)="onSlotClick($event)"
                (requestAppointment)="onRequestAppointment($event)"
                (showFitterClickEvent)="onFitterClick($event)"
                (fitterHoverEvent)="onFitterHover($event)"
              ></sc-appointment-common-list>
              <div class="list-footer-container">
                <p
                  *ngIf="unaffiliatedLink && !(loading$ | async)"
                  class="unaffiliated-garage"
                >
                  <a [href]="unaffiliatedLink">{{
                    'CustomerDeclaration.AppointmentSearchPage.unaffiliatedLinkText'
                      | translate
                  }}</a>
                </p>
              </div>
              <div
                class="underMapButtonList"
                *ngIf="displayUnderMapButtons() && isMobileResolution"
              >
                <sc-button
                  *ngIf="displayReturnButton()"
                  buttonType="mainWhite"
                  (click)="onReturnBtnClick()"
                  >{{
                    'CustomerDeclaration.AppointmentSearchPage.ReturnButton'
                      | translate
                  }}</sc-button
                >
                <sc-button
                  *ngIf="displayNoSlotsButton()"
                  (click)="onNoSlotsBtnClick()"
                  >{{
                    'CustomerDeclaration.AppointmentSearchPage.noSlotsFits'
                      | translate
                        : {
                            clientAppId: clientAppId
                          }
                  }}</sc-button
                >
              </div>
            </section>
          </mat-tab>
          <mat-tab>
            <ng-template mat-tab-label>{{
              'CustomerDeclaration.AppointmentSearchPage.toggleMap' | translate
            }}</ng-template>
            <section>
              <sc-gmap
                tourAnchor="intervention-map"
                currentMapType="DISPLAY_APPOINTMENTS_TIME"
                [defaultZoomLevel]="currentZoomLevel"
                [defaultMode]="
                  configurationService.getConfig()?.map?.defaultMode
                "
                [firstAppointments]="
                  appointmentSub.firstProposedAppointments$ | async
                "
                [firstOutsideAppointment]="getFirstOutsideAppointment() | async"
                [fitters]="currentFitters"
                [onMapChange]="onMapChange"
                [defaultLatitude]="mapCenter?.lat"
                [defaultLongitude]="mapCenter?.lon"
                [dotFitters]="appointmentSub.dotFitters$ | async"
                [errorDotFitters]="appointmentSub.noAppDotFitters$ | async"
                [locationPin]="
                  selectedSearchLocationGeoCodedData?.geoCoordinates
                "
                [countryViewBounds]="countryViewBounds"
                [fitterHover]="fitterHover"
                [fittersWithCard]="
                  appointmentSub.displayedNoAppointmentFitterCards$ | async
                "
                (zoomChange)="onZoomChange($event)"
                (centerChange)="onCenterChange($event)"
                (boundsChange)="onBoundsChange($event)"
                (appointmentClickEvent)="onSlotClick($event)"
                (fitterCardClickEvent)="onFitterCardClick($event)"
                [showPOI]="showPOI"
              ></sc-gmap>
              <div class="underMapButtonList" *ngIf="displayUnderMapButtons()">
                <sc-button
                  *ngIf="displayReturnButton()"
                  buttonType="mainWhite"
                  (click)="onReturnBtnClick()"
                  >{{
                    'CustomerDeclaration.AppointmentSearchPage.ReturnButton'
                      | translate
                  }}</sc-button
                >
                <sc-button
                  *ngIf="displayNoSlotsButton()"
                  (click)="onNoSlotsBtnClick()"
                  >{{
                    'CustomerDeclaration.AppointmentSearchPage.noSlotsFits'
                      | translate
                        : {
                            clientAppId: clientAppId
                          }
                  }}</sc-button
                >
              </div>
            </section>
          </mat-tab>
        </mat-tab-group>
        <sc-appointment-common-list
          *ngIf="!isMobileResolution"
          id="search-page-appointment-list"
          [filters]="filters$ | async"
          [groupBy]="
            (dossier$ | async)?.searchCriteria?.filters?.groupBy || 'fitters'
          "
          [selectedAppointment]="selectedAppointment"
          [displayReplacementVehicle]="displayReplacementVehicle"
          [canFilterOutsideIntervention]="hasOutsideInterventionFeature"
          [selectedDay]="selectedDay"
          [noBookDisplay]="noBookDisplay$ | async"
          [highlightBestScoreFitter]="highlightBestScoreFitter"
          [appointmentSubscription]="appointmentSub"
          [loading]="loading$ | async"
          [dossier]="dossier$ | async"
          [shouldDelayNoSlotFitterDisplay]="
            configurationService?.hasNoSlotFitterWithDelayDisplay()
          "
          (slotClickEvent)="onSlotClick($event)"
          (requestAppointment)="onRequestAppointment($event)"
          (showFitterClickEvent)="onFitterClick($event)"
          (fitterHoverEvent)="onFitterHover($event)"
        ></sc-appointment-common-list>
        <div class="list-footer-container" *ngIf="!isMobileResolution">
          <p
            *ngIf="unaffiliatedLink && !(loading$ | async)"
            class="unaffiliated-garage"
          >
            <a [href]="unaffiliatedLink">{{
              'CustomerDeclaration.AppointmentSearchPage.unaffiliatedLinkText'
                | translate
            }}</a>
          </p>
        </div>
        <div
          class="underMapButtonList"
          *ngIf="displayUnderMapButtons() && !isMobileResolution"
        >
          <sc-button
            *ngIf="displayReturnButton()"
            buttonType="mainWhite"
            (click)="onReturnBtnClick()"
            >{{
              'CustomerDeclaration.AppointmentSearchPage.ReturnButton'
                | translate
            }}</sc-button
          >
          <sc-button
            *ngIf="displayNoSlotsButton()"
            (click)="onNoSlotsBtnClick()"
            >{{
              'CustomerDeclaration.AppointmentSearchPage.noSlotsFits'
                | translate
                  : {
                      clientAppId: clientAppId
                    }
            }}</sc-button
          >
        </div>
      </div>
      <div
        *ngIf="shouldShowRightSide()"
        id="search-page-map-container"
        class="right"
      >
        <span
          *ngIf="shouldShowCalendarChoice()"
          id="showMapButton"
          tabindex="0"
          role="button"
          (click)="showMapChange()"
          (keydown.enter)="showMapChange()"
          (keydown.space)="showMapChange()"
        >
          <sc-icon
            aria-hidden="true"
            [name]="showDesktopMap ? 'calendar_hour' : 'map'"
            specificName="search-page-map-calendar"
          ></sc-icon>
          <span class="calendarMapLabel">{{
            showDesktopMap
              ? ('InsurerDeclarationIntervention.seeMapBtn' | translate)
              : ('InsurerDeclarationIntervention.seeCalendarBtn' | translate)
          }}</span>
        </span>
        <sc-calendar
          *ngIf="shouldShowCalendar()"
          [closestFitterAppointmentDateKey]="
            getFirstAppointmentDateString() | async
          "
          [highlightBestScoreFitter]="highlightBestScoreFitter"
          [defaultDuration]="getDuration(isReparation)"
          [appointmentSubscription]="appointmentSub"
          (selectDate)="onCalendarSelectDate($event)"
        ></sc-calendar>
        <div class="stickyRight">
          <sc-gmap
            tourAnchor="intervention-map"
            [ngClass]="{
              'hide-map': isMobileResolution
            }"
            currentMapType="DISPLAY_APPOINTMENTS_TIME"
            [defaultZoomLevel]="currentZoomLevel"
            [defaultMode]="configurationService.getConfig()?.map?.defaultMode"
            [firstAppointments]="
              appointmentSub.firstProposedAppointments$ | async
            "
            [firstOutsideAppointment]="getFirstOutsideAppointment() | async"
            [fitters]="currentFitters"
            [onMapChange]="onMapChange"
            [defaultLatitude]="mapCenter?.lat"
            [defaultLongitude]="mapCenter?.lon"
            [dotFitters]="appointmentSub.dotFitters$ | async"
            [errorDotFitters]="appointmentSub.noAppDotFitters$ | async"
            [locationPin]="selectedSearchLocationGeoCodedData?.geoCoordinates"
            [countryViewBounds]="countryViewBounds"
            [fitterHover]="fitterHover"
            [fittersWithCard]="
              appointmentSub.displayedNoAppointmentFitterCards$ | async
            "
            (zoomChange)="onZoomChange($event)"
            (centerChange)="onCenterChange($event)"
            (boundsChange)="onBoundsChange($event)"
            (appointmentClickEvent)="onSlotClick($event)"
            (fitterCardClickEvent)="onFitterCardClick($event)"
            [showPOI]="showPOI"
          ></sc-gmap>
          <div class="underMapButtonList" *ngIf="displayUnderMapButtons()">
            <sc-button
              *ngIf="displayReturnButton()"
              buttonType="mainWhite"
              (click)="onReturnBtnClick()"
              >{{
                'CustomerDeclaration.AppointmentSearchPage.ReturnButton'
                  | translate
              }}</sc-button
            >
            <sc-button
              *ngIf="displayNoSlotsButton()"
              (click)="onNoSlotsBtnClick()"
              >{{
                'CustomerDeclaration.AppointmentSearchPage.noSlotsFits'
                  | translate
                    : {
                        clientAppId: clientAppId
                      }
              }}</sc-button
            >
          </div>
        </div>
      </div>
      <div
        *ngIf="!shouldShowSearchBar()"
        class="change-address-button"
        tabindex="0"
        role="button"
        (click)="onClickChangeAddressButton()"
        (keydown.enter)="onClickChangeAddressButton()"
        (keydown.space)="onClickChangeAddressButton()"
      >
        <sc-icon aria-hidden="true" name="map_location"></sc-icon>
        <div class="text">
          {{
            selectedSearchLocationGeoCodedData?.address ??
              'CustomerDeclaration.AppointmentSearchPage.enterAddress'
              | translate
          }}
        </div>
      </div>
      <span
        class="loader-indicator-text"
        aria-live="polite"
        aria-atomic="true"
        role="status"
      >
        {{
          (loading$ | async)
            ? ('GlobalApplicativeConstants.accessibility.loaderIndicator'
              | translate)
            : ''
        }}
      </span>
    </div>
  `,
})
export class SearchPageComponent implements OnInit, OnDestroy {
  @Input() filterStyle: FilterDisplayStyle = 'with-background';
  @Input() showListTitle = false;
  @Input() allowPeriodFilter = true;
  @Input() withCalendar = true;
  @Input() fetchSlotsTimeout = 15000;
  @Input() showPOI = false;
  @Input() useLastFilters = true;

  @Output() bookAppointment = new EventEmitter<{
    fitter: Fitter;
    dossier: Dossier;
    modified: boolean;
  }>();
  @Output() requestAppointment = new EventEmitter<{
    dossier: Dossier;
    fitter: Fitter;
  }>();
  @Output() fitterSelect = new EventEmitter<{
    brandId: string;
    fitterId: string;
  }>();
  @Output() navigateToExitUriEvent = new EventEmitter<any>();

  days: readonly string[];

  //#region Declaring Address Locations
  selectedSearchLocationGeoCodedData: LocationDataGeocoded;
  selectedLocationData: LocationData;
  googleAutoCompleteResponse: LocationData[] = [];
  private readonly addressSuggestionsSubject$ = new Subject<string>();
  //#endregion Declaring Address Locations

  //#region List related objects
  currentFitters: Fitter[];
  allFitters: Fitter[] = [];
  allDotFitters: DotFitter[] = [];
  noAppFitters: DotFitter[];
  noBookDisplay$: Observable<boolean> = of(false);
  hasReplacementVehicleFeature = false;
  displayReplacementVehicle = true;
  displayGeneralistsToggle: Observable<boolean>;
  hasOutsideInterventionFeature = false;
  showMobileList = true;
  //#endregion List related objects

  //#region Map related Objects
  distanceSearch = 5000;
  mapCenter: GeoCoordinates;
  mapGeometry = new BehaviorSubject<BoundingBox>(null);
  countryViewBounds: CountryBounds;
  currentZoomLevel = 13;
  showDesktopMap = true;
  onMapChange = false;
  onMapMoveAddress = true;
  onMapMoveDotFitter = false;
  zoomHasChange = false;
  isFirstSearch = true;
  //#endregion Map related Objects

  //#region Calendar related objects
  selectedDay?: Date;
  isReparation = false;
  //#endregion Calendar related objects

  highlightBestScoreFitter = false;
  currentSearchAvance: SearchAvance = null;

  dossier$: Observable<Dossier>;
  filters$: Observable<InterventionParams>;
  currentDossier: Dossier;

  loading$ = new BehaviorSubject<boolean>(false);

  cachedFittersIds: string[] = [];
  selectedAppointment?: Appointment;

  appointmentSub: AppointmentsSubService;

  isMobileResolution = ScreenUtils.isMobileResolution();

  fitterHover: string;

  isLoadingSuggestions$ = new BehaviorSubject<boolean>(true);

  form: FormGroup = new FormGroup({
    address: new FormControl(
      '',
      [Validators.required],
      [
        CustomValidators.atLeastOneSuggestion(
          this.isLoadingSuggestions$,
          () => this.googleAutoCompleteResponse
        ),
      ]
    ),
  });

  unaffiliatedLink: string;

  isSinisterNumberContext = false;

  constructor(
    private readonly translator: TranslatorService,
    private readonly sandboxService: SandboxService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly featureFlippingService: FeatureFlippingManagerService,
    private readonly overlayService: AdaptableOverlayService,
    public configurationService: ConfigurationService,
    public bottomSheetService: BottomSheetService,
    private readonly appointmentSubBuilder: AppointmentSubscriptionBuilder,
    private readonly dossierSB: DossierSandboxService,
    private readonly declarationConditions: DeclarationConditions,
    private readonly focusManager: FocusManagerService,
    private readonly loadingService: LoadingService,
    private readonly storage: StorageService,
    private readonly tmService: TagManagerService,
    private readonly insuranceCoverSB: InsuranceCoverSandboxService,
    @Inject(CLIENT_APP_ID) public clientAppId: ClientAppId,
    @Inject(DOCUMENT) private readonly doc: Document
  ) {
    this.translator.getCurrentLang().subscribe((newFormattedLang) => {
      this.days = getLocaleDayNames(
        newFormattedLang,
        FormStyle.Standalone,
        TranslationWidth.Wide
      );
    });

    this.unaffiliatedLink =
      this.storage.getItem('redirect-na-uri') ||
      this.configurationService.getConfig().notAffiliatedLink;

    this.dossier$ = this.dossierSB.dossier$;

    this.filters$ = this.dossier$.pipe(
      map((dossier) => {
        return {
          outsideIntervention:
            dossier?.searchCriteria?.filters?.interventionParams
              ?.outsideIntervention ?? true,
          fitterIntervention:
            dossier?.searchCriteria?.filters?.interventionParams
              ?.fitterIntervention ?? true,
          replacementVehicle:
            dossier?.searchCriteria?.filters?.interventionParams
              ?.replacementVehicle ?? false,
          displayGeneralists:
            dossier?.searchCriteria?.filters?.interventionParams
              ?.displayGeneralists ?? false,

          displayGlassSpecialist:
            dossier?.searchCriteria?.filters?.interventionParams
              ?.displayGlassSpecialist ?? true,
        };
      })
    );

    this.dossier$.subscribe((newDossier) => (this.currentDossier = newDossier));

    const builder = this.appointmentSubBuilder
      .addInCenterObservable(
        this.dossier$.pipe(
          map(
            (d) =>
              d?.searchCriteria?.filters?.interventionParams?.fitterIntervention
          )
        )
      )
      .addAtHomeObservable(
        this.dossier$.pipe(
          map(
            (d) =>
              d?.searchCriteria?.filters?.interventionParams
                ?.outsideIntervention
          )
        )
      )
      .addReplacementVehicleObservable(
        this.dossier$.pipe(
          map(
            (d) =>
              d?.searchCriteria?.filters?.interventionParams?.replacementVehicle
          )
        )
      )
      .addGeneralistFittersObservable(
        this.dossier$.pipe(
          map(
            (d) =>
              d?.searchCriteria?.filters?.interventionParams
                ?.displayGeneralists &&
              d?.searchCriteria?.filters?.groupBy === 'fitters'
          )
        )
      )
      .addSpecialistFittersObservable(
        this.dossier$.pipe(
          map(
            (d) =>
              d?.searchCriteria?.filters?.interventionParams
                ?.displayGlassSpecialist
          )
        )
      )
      .addTimeFilterObservable(
        this.dossier$.pipe(map((d) => d?.searchCriteria?.filters?.period))
      )
      .addAppointmentsMapObservable(
        this.dossier$.pipe(
          map((d) => d?.searchCriteria?.filters?.groupBy as AppointmentsGroupBy)
        )
      )
      .checkCutOffFeature()
      .checkMobilityService()
      .checkReplacementVehicleService()
      .removeDuplicates()
      .fetchTimeout(15000);

    this.appointmentSub = builder.build();

    this.onBoundsChange = throttle(this.onBoundsChangeThrottled, 1000, {
      leading: false,
      trailing: true,
    });
    this.onZoomChange = throttle(this.onZoomChangeThrottled, 1000, {
      leading: false,
      trailing: true,
    });
    this.onCenterChange = throttle(this.onCenterChangeThrottled, 1000, {
      leading: false,
      trailing: true,
    });

    this.sandboxService.getCountryBounds().subscribe((result) => {
      this.countryViewBounds = result;
    });
  }

  // DO NOT DELETE
  onZoomChange(event) {}
  onCenterChange(event) {}
  onBoundsChange(event) {}

  //#region Hooks
  ngOnInit(): void {
    // Init map
    this.mapCenter = {
      lat: this.configurationService.getConfig().map.defaultLatitude,
      lon: this.configurationService.getConfig().map.defaultLongitude,
    };

    // Init subscriptions
    this.appointmentSub.initSubscriptions();

    this.addressSuggestionsSubject$
      .pipe(
        debounceTime(500),
        filter((value) => value.length >= 3)
      )
      .subscribe((value: string) => {
        this.sandboxService.getPlacesFromAddress(value).subscribe({
          next: (places: LocationData[]) => {
            this.googleAutoCompleteResponse = places;
            this.isLoadingSuggestions$.next(false);
            this.isLoadingSuggestions$.next(true);
            this.changeDetectorRef.markForCheck();
          },
          error: () => {
            this.googleAutoCompleteResponse = [];
            this.isLoadingSuggestions$.next(false);
            this.isLoadingSuggestions$.next(true);
            this.changeDetectorRef.markForCheck();
          },
        });
      });

    this.appointmentSub.dossierSB.dossier$
      .pipe(
        filter((dossier) => !!dossier),
        take(1)
      )
      .subscribe((dossier) => {
        this.currentDossier = dossier;

        this.isSinisterNumberContext =
          !!dossier?.policyInformation?.sinisterNumber;

        this.selectedSearchLocationGeoCodedData =
          dossier?.searchCriteria?.address ?? dossier?.customer?.location;

        this.form
          ?.get('address')
          ?.setValue(this.selectedSearchLocationGeoCodedData?.address ?? '');

        this.googleAutoCompleteResponse = this
          .selectedSearchLocationGeoCodedData?.address
          ? [this.selectedSearchLocationGeoCodedData]
          : [];
        this.isLoadingSuggestions$.next(false);
        setTimeout(() => {
          this.isLoadingSuggestions$.next(true);
        }, 0);

        this.isReparation = dossier?.claim?.operationType === 'Repair';

        this.noBookDisplay$ = this.insuranceCoverSB
          .getCanBookWithInsurance()
          .pipe(
            map(
              (canBookWithInsurance) =>
                !canBookWithInsurance ||
                !this.declarationConditions.canBookAppointment(
                  dossier?.claim?.glass
                )
            )
          );

        if (this.selectedSearchLocationGeoCodedData) {
          if (this.selectedSearchLocationGeoCodedData.geoCoordinates) {
            this.mapCenter =
              this.selectedSearchLocationGeoCodedData.geoCoordinates;
          }
          this.onAddressChange(this.selectedSearchLocationGeoCodedData, true);
        } else if (!this.shouldShowSearchBar()) {
          this.onClickChangeAddressButton();
        }
      });

    this.appointmentSub.fittersInCache$.subscribe((fittersInCache) => {
      if (DEBUG) console.log('>>>fittersInCache ' + fittersInCache.length);
      this.cachedFittersIds = fittersInCache;
    });

    this.appointmentSub.terminalSearchStatus$.subscribe((s) => {
      if (DEBUG) console.log('>>>terminalSearchStatus ' + s);
      this.loading$.next(false);
    });

    this.appointmentSub.searchAvance$.subscribe((searchAvance) => {
      if (searchAvance.nbDisplay > -1) {
        this.currentSearchAvance = searchAvance;
        if (DEBUG)
          console.log('>>>searchAvance :\n' + JSON.stringify(searchAvance));
        if (
          this.currentSearchAvance.requestDisplayList.some((r) =>
            this.currentSearchAvance.receiveList.includes(r)
          )
        ) {
          this.loading$.next(false);
        }
      }
    });

    this.appointmentSub.fetchFreeSlotsError$.subscribe((s) => {
      if (DEBUG) console.log('>>>fetchFreeSlotsError ' + s);
      this.loading$.next(false);
    });

    this.appointmentSub.choseAppointmentsMap$.subscribe((appointmentsMap) => {
      if (DEBUG)
        console.log('>>>choseAppointmentsMap' + appointmentsMap.values.length);
      this.changeDetectorRef.markForCheck();
    });

    // Subscribe to ALL fitters to track distances
    this.appointmentSub.fitters$.subscribe((allFitters) => {
      if (DEBUG) console.log('>>>allFitters' + allFitters.length);
      this.allFitters = allFitters;
      this.changeDetectorRef.markForCheck();
    });

    // Subscribe to dotFitters to track distances from backend
    this.appointmentSub.dotFitters$.subscribe((dotFitters) => {
      if (DEBUG) console.log('>>>dotFitters' + dotFitters.length);
      this.allDotFitters = dotFitters;
      this.changeDetectorRef.markForCheck();
    });

    this.appointmentSub.displayedFitters$.subscribe((displayedFitters) => {
      if (DEBUG) console.log('>>>displayedFitters' + displayedFitters.length);
      this.currentFitters = displayedFitters;
      this.onMapMoveDotFitter = true;

      this.changeDetectorRef.markForCheck();
    });

    // Init feature flips
    this.hasOutsideInterventionFeature =
      this.featureFlippingService.isFeatureActivated(
        'insurer-can-declare-in-mobility'
      );

    this.hasReplacementVehicleFeature =
      this.featureFlippingService.isFeatureActivated(
        'can-declare-replacement-vehicle-insurer'
      );

    this.displayReplacementVehicle =
      this.hasReplacementVehicleFeature && !this.isReparation;

    this.highlightBestScoreFitter =
      this.featureFlippingService.isFeatureActivated(
        'b2b-highlight-best-scored-fitter'
      );

    this.displayGeneralistsToggle = this.appointmentSub.dotFitters$.pipe(
      map(
        (fitters) =>
          (!fitters?.length &&
            this.featureFlippingService.brandFeatureActivatedExists(
              'fitter-generalist-service'
            )) ||
          fitters.some((f) =>
            this.featureFlippingService.isBrandFeatureActivated(
              f.brandId,
              'fitter-generalist-service'
            )
          )
      )
    );
  }

  ngOnDestroy(): void {
    this.appointmentSub.destroy();
    this.addressSuggestionsSubject$.complete();
  }
  //#endregion Hooks

  @HostListener('window:resize', ['$event'])
  adaptOnResize(): void {
    this.isMobileResolution = ScreenUtils.isMobileResolution();

    this.isLoadingSuggestions$.next(false);
    setTimeout(() => this.isLoadingSuggestions$.next(true), 0);
  }

  isModificationMode() {
    return (
      this.currentDossier?.appointment?.status === 'MODIFIED' ||
      this.currentDossier?.requestAppointment?.status === 'MODIFIED'
    );
  }

  askFocus(query: string) {
    setTimeout(() => {
      this.focusManager.focusFirstElementAllowed(query, true);
    }, 0);
  }

  //#region Display checks
  shouldShowSearchBar() {
    return !this.isMobileResolution;
  }

  shouldShowTopFilters() {
    return this.isMobileResolution;
  }

  shouldShowLeftSide() {
    return !this.isMobileResolution || this.showMobileList;
  }

  shouldShowLeftFilters() {
    return !this.isMobileResolution;
  }

  shouldShowRightSide() {
    return !this.isMobileResolution;
  }

  shouldShowCalendarChoice() {
    return !this.isMobileResolution && this.withCalendar;
  }

  shouldShowCalendar() {
    return (
      !this.isMobileResolution && !this.showDesktopMap && this.withCalendar
    );
  }

  shouldShowMap() {
    return this.isMobileResolution || this.showDesktopMap;
  }
  //#endregion Display checks

  //#region Address Region
  onUpdateAddressSuggestions(value: string) {
    this.addressSuggestionsSubject$.next(value);
  }

  onAddressChange(locationData: LocationData, launchSearch: boolean) {
    if (this.loading$.getValue()) return;

    this.selectedLocationData = locationData;
    this.sandboxService
      .getGeoCodeFromPlaceId(this.selectedLocationData.placeId)
      .pipe(
        take(1),
        tap(
          (locationGeocoded) =>
            (this.selectedSearchLocationGeoCodedData = locationGeocoded)
        ),
        switchMap(() => {
          return this.selectedSearchLocationGeoCodedData?.addressElements
            ?.postalCode?.length
            ? of(this.selectedSearchLocationGeoCodedData)
            : this.sandboxService.getPostalCodeFromGeoCode(
                this.selectedSearchLocationGeoCodedData?.geoCoordinates
              );
        }),
        take(1),
        tap((value) => {
          this.selectedSearchLocationGeoCodedData = {
            ...this.selectedSearchLocationGeoCodedData,
            address: value.address,
            addressElements: {
              ...this.selectedSearchLocationGeoCodedData.addressElements,
              postalCode: value?.addressElements?.postalCode,
            },
          };
          this.mapCenter =
            this.selectedSearchLocationGeoCodedData.geoCoordinates;
        }),
        switchMapTo(this.appointmentSub.dossierSB.dossier$),
        take(1),
        switchMap((dossier) =>
          this.appointmentSub.dossierSB.patchDossier({
            ...dossier,
            searchCriteria: {
              ...dossier?.searchCriteria,
              address: this.selectedSearchLocationGeoCodedData,
            },
          })
        ),
        switchMap(() => this.appointmentSub.dossierSB.updateDossier())
      )
      .subscribe((dossier) => {
        if (launchSearch) {
          if (DEBUG) console.log('>>> onSearch from adresse change');
          this.onMapMoveAddress = true;
          this.onSearch(false);
        }
      });
  }
  //#endregion Address Region

  //#region Searching functions
  onSearch(onMapChange = false) {
    if (this.loading$.getValue()) return;

    this.onMapChange = onMapChange;
    this.changeDetectorRef.markForCheck();

    if (!this.selectedLocationData) {
      this.selectedLocationData = this.selectedSearchLocationGeoCodedData;
    }

    this.changeDetectorRef.markForCheck();

    this.fetchFreeSlots(this.onMapChange);
  }

  fetchFreeSlots(useMapGeometry: boolean) {
    if (DEBUG)
      console.log(
        '>>>fetchFreeSlots useMapGeometry=' +
          useMapGeometry +
          ' exclude=' +
          JSON.stringify(this.cachedFittersIds)
      );

    if (
      this.selectedSearchLocationGeoCodedData?.address &&
      !this.loading$.getValue()
    ) {
      this.loading$.next(true);
      this.appointmentSub.clearSearchAvance();
      if (!useMapGeometry) this.appointmentSub.clearMobileFitters();

      const usedMapCenter = !this.showDesktopMap
        ? this.selectedSearchLocationGeoCodedData.geoCoordinates
        : this.mapCenter;

      this.mapGeometry
        .pipe(
          // We wait for the center to be inside the map bounds, because if the search is launched too quickly
          // They will not be synchronized and potentially show in completly different places
          filter(
            (geometry) =>
              !useMapGeometry ||
              !geometry ||
              (usedMapCenter.lat > geometry?.sw.lat &&
                usedMapCenter.lon > geometry?.sw.lon &&
                usedMapCenter.lat < geometry?.ne.lat &&
                usedMapCenter.lon < geometry?.ne.lon)
          ),
          switchMap((geometry) =>
            this.noBookDisplay$.pipe(
              map((hideSlots) => ({ geometry, hideSlots }))
            )
          ),
          take(1)
        )
        .subscribe(({ geometry, hideSlots }) => {
          // Build known fitters from ALL fitters (not just displayed ones)
          // Combine Fitter (with duration) and DotFitter (distance only)
          const knownFittersFromFitters =
            this.allFitters
              ?.filter(
                (f) =>
                  f.distance != null &&
                  f.duration != null &&
                  f.distance > 0 &&
                  f.duration > 0
              )
              ?.map((f) => ({
                id: f.id,
                distance: f.distance,
                duration: f.duration,
              })) || [];

          const knownFittersFromDotFitters =
            this.allDotFitters
              ?.filter((df) => df.distance != null && df.distance > 0)
              ?.map((df) => ({
                id: df.id,
                distance: df.distance,
                // duration not available in DotFitter
              })) || [];

          // Merge both lists, prioritizing Fitters (with duration) over DotFitters
          const knownFittersMap = new Map<
            string,
            { id: string; distance: number; duration?: number }
          >();

          // Add DotFitters first (lower priority - no duration)
          knownFittersFromDotFitters.forEach((kf) =>
            knownFittersMap.set(kf.id, kf)
          );

          // Add Fitters second (higher priority - has duration, will overwrite DotFitters)
          knownFittersFromFitters.forEach((kf) =>
            knownFittersMap.set(kf.id, kf)
          );

          const knownFitters = Array.from(knownFittersMap.values());

          if (DEBUG) {
            console.log(
              '>>>knownFitters from Fitters: ' + knownFittersFromFitters.length
            );
            console.log(
              '>>>knownFitters from DotFitters: ' +
                knownFittersFromDotFitters.length
            );
            console.log(
              '>>>knownFitters total (deduplicated): ' + knownFitters.length
            );
          }

          const fetchSlotsParams = {
            searchDates: {
              startDate: new Date(),
              endDate: null,
            },
            claim: this.currentDossier?.claim,
            searchLocation: {
              distance: this.distanceSearch,
              geometry: useMapGeometry ? geometry : null,
              address: this.selectedSearchLocationGeoCodedData,
              mapCenter: usedMapCenter,
              options: {
                shouldWidenCache: true,
                shouldWidenSearch: !useMapGeometry,
              },
              knownFitters: knownFitters.length ? knownFitters : undefined,
            },
            excludeFitters: this.cachedFittersIds,
            outsideIntervention: null,
            replacementVehicle:
              this.currentDossier?.claim?.operationType === 'Replace',
            returnFittersOnly: hideSlots,
          };

          if (this.isFirstSearch) {
            this.onMapMoveAddress = true;
          }
          this.isFirstSearch = false;

          this.appointmentSub.fetchFreeSlots(fetchSlotsParams);
        });
    }
  }
  //#endregion Searching functions

  //#region Calendar Region
  getDuration(isReparation: boolean) {
    const durationObj = getTranslatableDuration(
      this.configurationService.getConfig().defaultInterventionDuration[
        isReparation === true ? 'repair' : 'replace'
      ]
    );
    return this.translator.getInstantTranslation(
      durationObj?.value,
      durationObj.param
    );
  }

  getFirstAppointmentDateString(): Observable<string> {
    return this.appointmentSub.firstAppointment$.pipe(
      map((a) => new Date(a?.time || 0).toDateString())
    );
  }

  onCalendarSelectDate(newSelectedDay: Date) {
    this.selectedDay = newSelectedDay;
  }
  //#endregion Calendar Region

  //#region Map functions
  showMapChange() {
    if (this.withCalendar) {
      this.showDesktopMap = !this.showDesktopMap;
    }
  }

  onBoundsChangeThrottled(newBoundsEvent: MapOnBoundsChangeEvent) {
    if (DEBUG) console.log('>>>onBoundsChange');
    this.mapGeometry.next(gmapBoundsToBoundingBox(newBoundsEvent.bounds));
    if (this.zoomHasChange) {
      this.zoomHasChange = false;
      if (!this.onMapMoveAddress && !this.onMapMoveDotFitter) {
        this.onSearch(true);
        if (DEBUG) console.log('>>> onSearch from Zoom change');
      }
    }
    this.onMapMoveAddress = false;
    this.onMapMoveDotFitter = false;
  }

  onZoomChangeThrottled(newZoomEvent: MapOnZoomChangeEvent) {
    this.currentZoomLevel = newZoomEvent.zoom;
    if (
      this.selectedSearchLocationGeoCodedData &&
      !newZoomEvent.isAuto &&
      !this.loading$.getValue()
    ) {
      this.zoomHasChange = true;
    }
  }

  onCenterChangeThrottled(newCenterEvent: MapOnCenterChangeEvent): void {
    this.mapCenter = newCenterEvent.center;
    if (!this.loading$.getValue() && !newCenterEvent.isAuto) {
      if (!this.onMapMoveAddress && !this.onMapMoveDotFitter) {
        this.onSearch(true);
        if (DEBUG) console.log('>>> onSearch from map center move');
      }
    }
    this.onMapMoveAddress = false;
    this.onMapMoveDotFitter = false;
  }

  getAppointmentsByFitter(): Observable<Map<string, Appointment[]>> {
    return this.appointmentSub.appointmentsByFitter$;
  }

  getFirstOutsideAppointment() {
    return this.appointmentSub.firstProposedOutsideIntervention$;
  }
  //#endregion Map functions

  //#region Appointment List functions
  onSlotClick(clickedAppointment: Appointment) {
    const fitter = this.currentFitters?.find(
      (f) => f?.id === clickedAppointment?.fitterId
    );

    const fitterLocation: LocationDataGeocoded =
      AppointmentUtils.getFitterLocation(fitter);
    const interventionLocation = !clickedAppointment?.outsideIntervention
      ? fitterLocation
      : this.selectedSearchLocationGeoCodedData;

    this.selectedAppointment = {
      ...clickedAppointment,
      interventionLocation: interventionLocation,
    };

    if (
      this.featureFlippingService.isFeatureActivated(
        'tag-commander-global-buffer'
      )
    ) {
      this.tmService.pushTag({
        isPopIn: true,
        pageName: 'PopinCreneau',
      });
    }

    let isModifMode = false;
    this.overlayService
      .showOverlay({
        type: 'appointment-validation',
        input: {
          displayReplacementVehicleSelection:
            this.hasReplacementVehicleFeature &&
            this.featureFlippingService.isBrandFeatureActivated(
              clickedAppointment.brandId,
              'can-declare-replacement-vehicle-fitter'
            ),
          onlyReplacementVehicle:
            this.currentDossier?.searchCriteria?.filters?.interventionParams
              .replacementVehicle,
          location: interventionLocation,
          appointment: this.selectedAppointment,
          customer: this.currentDossier?.customer,
          fitter: this.appointmentSub.rawFitters$
            .getValue()
            .find((f) => f.id === clickedAppointment.fitterId),
        },
      })
      .pipe(
        take(1),
        switchMap((outputData) => {
          if (outputData.type === 'cancel') {
            const slotIdToFocus =
              this.sandboxService.formatSlotElementId(clickedAppointment);
            const slotToFocus = this.doc.getElementById(slotIdToFocus);

            if (slotToFocus) {
              this.focusManager.focusFirstElementAllowed(slotToFocus, true);
            }

            return EMPTY;
          }

          return of(outputData.data);
        }),
        switchMap((outputData) =>
          this.sandboxService
            .getItinerary(
              this.currentDossier?.searchCriteria?.address?.placeId,
              fitterLocation.geoCoordinates,
              dayjs(this.selectedAppointment.time).unix()
            )
            .pipe(map((itinerary) => [outputData, itinerary] as const))
        ),
        map(([outputData, itinerary]): Dossier => {
          const previousAppointments = [
            ...(this.currentDossier?.previousAppointments || []),
          ];

          if (this.isModificationMode()) {
            if (this.currentDossier?.appointment) {
              previousAppointments.push({
                ...this.currentDossier?.appointment,
              });
            }
            isModifMode = true;
          }

          const bookedDossier: Dossier = {
            ...this.currentDossier,
            customer: {
              ...outputData.customer,
            },
            appointment: {
              ...this.selectedAppointment,
              interventionLocation: outputData.location,
              isReplacementVehicleSelected: outputData.replacementVehicle,
              itinerary: itinerary,
            },
            previousAppointments: previousAppointments,
            searchCriteria: {
              ...this.currentDossier?.searchCriteria,
              address: this.selectedAppointment?.outsideIntervention
                ? outputData.location
                : this.currentDossier?.searchCriteria?.address,
            },
          };

          return bookedDossier;
        }),
        take(1),
        tap(() => this.changeDetectorRef.markForCheck())
      )
      .subscribe((result) => {
        const fitter = this.appointmentSub.rawFitters$
          .getValue()
          .find((f) => f.id === result.appointment?.fitterId);
        this.bookAppointment.emit({
          fitter: fitter,
          dossier: result,
          modified: isModifMode,
        });
      });
  }

  onRequestAppointment(fitterId: string) {
    const fitter = this.appointmentSub.rawFitters$
      .getValue()
      .find((f) => f.id === fitterId);

    if (
      this.featureFlippingService.isFeatureActivated(
        'tag-commander-global-buffer'
      )
    ) {
      this.tmService.pushTag({
        isPopIn: true,
        pageName: 'PopinCoordonees',
      });
    }

    this.overlayService
      .showOverlay({
        type: 'appointment-validation',
        input: {
          displayReplacementVehicleSelection:
            this.hasReplacementVehicleFeature &&
            this.featureFlippingService.isBrandFeatureActivated(
              fitter.brandId,
              'can-declare-replacement-vehicle-fitter'
            ),
          onlyReplacementVehicle:
            this.currentDossier?.searchCriteria?.filters?.interventionParams
              .replacementVehicle,
          location: this.selectedSearchLocationGeoCodedData,
          customer: this.currentDossier?.customer,
          fitter: fitter,
        },
      })
      .pipe(
        take(1),
        map((outputData): 'cancel' | Dossier => {
          if (outputData.type === 'success') {
            const bookedDossier: Dossier = {
              ...this.currentDossier,
              customer: {
                ...outputData.data.customer,
              },
              appointment: null,
              requestAppointment: {
                fitterId: fitter.id,
                brandId: fitter.brandId,
                timezone: fitter.timezone,
                outsideIntervention:
                  outputData.data.locationChoice === 'outside',
                interventionLocation: outputData.data.location,
                isReplacementVehicleSelected:
                  outputData.data.replacementVehicle,
              },
            };

            return bookedDossier;
          } else {
            return 'cancel';
          }
        }),
        take(1),
        tap(() => this.changeDetectorRef.markForCheck())
      )
      .subscribe((result) => {
        if (result !== 'cancel') {
          this.requestAppointment.emit({ dossier: result, fitter: fitter });
        }
      });
  }

  onFitterCardClick(fitter: Fitter) {
    if (
      (this.featureFlippingService.isBrandFeatureActivated(
        fitter.brandId,
        'b2c-allow-appointment-request'
      ) &&
        this.clientAppId === 'customer') ||
      (this.featureFlippingService.isBrandFeatureActivated(
        fitter.brandId,
        'b2b-allow-appointment-request'
      ) &&
        this.clientAppId === 'insurer')
    ) {
      this.onRequestAppointment(fitter.id);
    }
  }

  onFitterClick(fitter: { brandId: string; fitterId: string }) {
    this.appointmentSub.fittersSB
      .selectFitterById(fitter.brandId, fitter.fitterId)
      .pipe(take(1))
      .subscribe(() => {
        this.fitterSelect.emit({
          brandId: fitter.brandId,
          fitterId: fitter.fitterId,
        });
      });
  }

  onToggleChange(newValue: boolean) {
    this.showMobileList = newValue;
    this.changeDetectorRef.markForCheck();
    setTimeout(() => {
      this.focusManager.focusFirstElementAllowed(
        `sc-search-page .sides ${newValue ? '.left' : '.gmap-container'}`,
        true
      );
    }, 100);
  }

  onClickAddressButton() {
    this.mapGeometry.next(null);
    if (!this.loading$.getValue()) {
      this.onMapMoveAddress = true;
      if (this.selectedSearchLocationGeoCodedData) {
        if (DEBUG)
          console.log(
            '>>> search from onClickAddressButton useMapGeometry=false'
          );
        if (this.selectedSearchLocationGeoCodedData.geoCoordinates) {
          this.mapCenter =
            this.selectedSearchLocationGeoCodedData.geoCoordinates;
        }
        this.fetchFreeSlots(false);
      }
    }
  }

  onClickChangeAddressButton() {
    this.bottomSheetService
      .showSelectAddress({ place: this.selectedSearchLocationGeoCodedData })
      .subscribe((data) => {
        if (data) {
          this.mapGeometry.next(null);
          if (DEBUG)
            console.log(
              '>>> search from onClickChangeAddressButton useMapGeometry=false'
            );
          this.onAddressChange(data.place, this.isMobileResolution); // change false to true for automatic relaunch
        }
      });
  }

  onFitterHover(event) {
    if (this.fitterHover != event) {
      this.fitterHover = event;
    }
  }
  //#endregion Appointment List functions

  async navigateToExitUri(dossierForContractManager?: any) {
    this.navigateToExitUriEvent.emit(dossierForContractManager);
  }

  onReturnBtnClick() {
    this.abandonAndExit(
      this.translator.getInstantTranslation(
        'CustomerDeclaration.AppointmentSearchPage.ReturnButton'
      )
    );
  }

  onNoSlotsBtnClick() {
    this.abandonAndExit(
      this.translator.getInstantTranslation(
        'CustomerDeclaration.AppointmentSearchPage.noSlotsFits',
        {
          clientAppId: this.clientAppId,
        }
      )
    );
  }

  abandonAndExit(justification: string) {
    this.loadingService.setLoading(true);
    const resultDossierObs = this.dossierSB
      .abandonDossier({
        id: this.currentDossier.id,
        abandonmentComment: justification,
      })
      .pipe(map((result) => result?.dossierForContractManager));
    resultDossierObs.subscribe({
      next: (dossierForContractManager) => {
        this.navigateToExitUri(dossierForContractManager);
      },
      error: (error) => {
        this.loadingService.setLoading(false);
        console.error('Error on abandon Dossier ! : ');
        console.error(error);
      },
    });
  }

  onGpdrInfo() {
    this.overlayService.showOverlay({
      type: 'gpdr-info-redirect',
      input: {
        redirectUrl: this.configurationService?.getConfig()?.gdpr?.legalUri,
      },
    });
  }

  displayNoSlotsButton() {
    return (
      this.featureFlippingService.isFeatureActivated(
        'display-undermap-noslot-fits-btn'
      ) ||
      (this.featureFlippingService.isFeatureActivated(
        'display-undermap-noslot-fits-btn-with-condition'
      ) &&
        !!this.currentDossier?.policyInformation?.sinisterNumber)
    );
  }

  displayReturnButton() {
    return this.featureFlippingService.isFeatureActivated(
      'display-undermap-return-btn'
    );
  }

  displayUnderMapButtons() {
    return (
      this.featureFlippingService.isFeatureActivated(
        'display-undermap-noslot-fits-btn'
      ) ||
      this.featureFlippingService.isFeatureActivated(
        'display-undermap-return-btn'
      ) ||
      this.featureFlippingService.isFeatureActivated(
        'display-undermap-noslot-fits-btn-with-condition'
      )
    );
  }
}
